import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { splitTitle } from "./Services";

export default function Inventory({ onEnquire, content }) {
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/vehicles")
      .then((res) => setVehicles(res.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoaded(true));
  }, []);

  const visible = useMemo(
    () => (filter === "All" ? vehicles : vehicles.filter((v) => v.brand === filter)),
    [filter, vehicles]
  );

  const filters = useMemo(
    () => ["All", ...new Set(vehicles.map((v) => v.brand).filter(Boolean))],
    [vehicles]
  );

  const eyebrow = content?.inventory_eyebrow || "02 /// THE FLOOR";
  const [titleA, titleB] = splitTitle(content?.inventory_title || "Vehicles For Sale");

  return (
    <section id="inventory" className="border-y border-[#263342] bg-[#0D1117] py-24 sm:py-32" data-testid="inventory-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-end justify-between gap-8"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-4">{eyebrow}</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-100">
              {titleA} <span className="text-outline">{titleB}</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="inventory-filter-chips">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`inventory-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                  filter === f
                    ? "border-crimson bg-crimson/10 text-crimson"
                    : "border-[#263342] text-slate-400 hover:border-slate-500 hover:text-slate-200"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inventory-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((v, i) => (
              <motion.article
                layout
                key={v.id}
                data-testid={`inventory-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-panel border border-[#263342] hover:border-crimson/50 transition-colors overflow-hidden"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.title}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161D26] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 bg-[#0A0D12]/85 backdrop-blur px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson border border-crimson/40">
                    {v.tag}
                  </span>
                </div>
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">{v.brand}</p>
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100 leading-tight">
                    {v.title}
                  </h3>
                  <p className="mt-3 font-mono text-xs tracking-[0.12em] text-slate-400">{v.specs}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-display font-bold text-2xl text-crimson" data-testid={`inventory-price-${i}`}>{v.price}</span>
                    <button
                      data-testid={`inventory-enquire-${i}`}
                      onClick={() => onEnquire({ service: "Vehicle Purchase Enquiry", message: `I'm interested in the ${v.title} (${v.price}). Please contact me with details.` })}
                      className="font-mono text-xs uppercase tracking-[0.2em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
                    >
                      Enquire
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {loaded && visible.length === 0 && (
          <p className="mt-10 font-mono text-sm text-slate-500" data-testid="inventory-empty">No vehicles in this category right now.</p>
        )}
      </div>
    </section>
  );
}
